import {
  ALL_TXN_BENCHMARK_COLUMN_KEYS,
  appendEquityExtraBenchmarkColumns,
  appendTxnBenchmarkColumns,
  EQUITY_EXTRA_BENCHMARK_COLUMN_ORDER,
} from "../../../utils/reports/transactionBenchmarkColumns";
import { appendAdjustedCostPriceColumn } from "../../../utils/reports/reportHoldingColumns";
import { REPORT_GAIN_TYPE_COLUMN_LABEL } from "../../../utils/reports/reportDisplayLabels";
import {
  appendHoldingLevelCostColumns,
  orderTransactionCostBenchmarkAndMemberColumns,
  withTxnMetadataAndBenchmarkColumns,
} from "../../../utils/reports/transactionMetadataColumns";
import {
  AIF_ALL_COMMON_COLUMNS,
  AIF_CONSOLIDATED_COLUMNS,
  AIF_CONSOLIDATED_REPORT,
  AIF_TABLE_SEGMENT,
  AIF_TRANSACTION_COLUMNS,
  AIF_UNREALIZED_HOLDING_COLUMNS,
  FieldColumn,
  getColumnsForAifReportType,
  INVESTOR_COLUMN,
} from "./aifReportConfig";

export const REPORT_BUILDER_VISIBLE_COLUMN_COUNT = 7;

const UNREALIZED_CAGR_COLUMN: FieldColumn = { key: "cagr", value: "CAGR", minWidth: 96 };

const PNL_COLUMN_KEYS = new Set([
  "gainLoss",
  "gainLossPercentage",
  "gainLossPerc",
  "unrealisedPNL",
  "unrealisedPNLPercentage",
  "realisedPnl",
  "realisedPNLPercentage",
]);

const UNREALIZED_HOLDING_TYPE_COLUMN: FieldColumn = {
  key: "type",
  value: REPORT_GAIN_TYPE_COLUMN_LABEL,
  locked: true,
  minWidth: 132,
};

const REPORT_DAYS_COLUMN: FieldColumn = {
  key: "days",
  value: "Avg. Days",
  minWidth: 88,
};

const REPORT_COST_COLUMN_KEYS = [
  "stt",
  "stampDuty",
  "otherExpenses",
  "brokerage",
] as const;

const AIF_ALL_REPORT_AFTER_ADJ_COLUMNS: FieldColumn[] = [
  { key: "xirr", value: "XIRR", minWidth: 96 },
  { key: "cagr", value: "CAGR", minWidth: 96 },
];

const AIF_ALL_HIDDEN_COLUMN_KEYS = new Set(["isin"]);

const withUnrealizedCagrColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (cols.some((c) => c.key === "cagr")) return cols;
  const without = cols.filter((c) => c.key !== "cagr");
  const pnlIdx = without.findIndex((c) => PNL_COLUMN_KEYS.has(c.key));
  const insertAt = pnlIdx >= 0 ? pnlIdx : without.length;
  return [
    ...without.slice(0, insertAt),
    UNREALIZED_CAGR_COLUMN,
    ...without.slice(insertAt),
  ];
};

const withUnrealizedHoldingTypeColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (!cols.length) return cols;

  let result = cols.map((c) =>
    c.key === "type" ? { ...c, value: REPORT_GAIN_TYPE_COLUMN_LABEL } : c,
  );

  if (result.some((c) => c.key === "type")) {
    return withUnrealizedCagrColumn(result);
  }

  const nameIdx = result.findIndex((c) => c.key === "schemeName");
  const insertAt = nameIdx >= 0 ? nameIdx + 1 : 1;

  return withUnrealizedCagrColumn([
    ...result.slice(0, insertAt),
    UNREALIZED_HOLDING_TYPE_COLUMN,
    ...result.slice(insertAt),
  ]);
};

const withTxnSchemeNameColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (cols.some((c) => c.key === "schemeName")) return cols;
  return [
    { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 220 },
    ...cols,
  ];
};

const ensureReportDaysColumn = (columns: FieldColumn[]): FieldColumn[] => {
  if (columns.some((c) => c.key === "days")) return columns;
  return [...columns, REPORT_DAYS_COLUMN];
};

const stripDaysColumn = (columns: FieldColumn[]): FieldColumn[] =>
  columns.filter((c) => c.key !== "days");

const orderDaysColumnBeforeStt = (columns: FieldColumn[]): FieldColumn[] => {
  const withoutDays = columns.filter((c) => c.key !== "days");
  const daysCol = columns.find((c) => c.key === "days") ?? REPORT_DAYS_COLUMN;
  const firstCostIdx = withoutDays.findIndex((c) =>
    REPORT_COST_COLUMN_KEYS.includes(
      c.key as (typeof REPORT_COST_COLUMN_KEYS)[number],
    ),
  );
  const insertAt = firstCostIdx >= 0 ? firstCostIdx : withoutDays.length;
  return [
    ...withoutDays.slice(0, insertAt),
    daysCol,
    ...withoutDays.slice(insertAt),
  ];
};

const stripMemberNameColumn = (cols: FieldColumn[]): FieldColumn[] =>
  orderTransactionCostBenchmarkAndMemberColumns(cols, {
    includeMemberName: false,
  }) as FieldColumn[];

const orderTrailingColumnsAfterAdjCost = (
  columns: FieldColumn[],
  trailingColumns: FieldColumn[],
): FieldColumn[] => {
  const trailingKeySet = new Set(trailingColumns.map((c) => c.key));
  const without = columns.filter((c) => !trailingKeySet.has(c.key));
  const trailingCols = trailingColumns.map((def) => {
    const existing = columns.find((c) => c.key === def.key);
    return existing ?? def;
  });
  const adjIdx = without.findIndex((c) => c.key === "adjustedCostPrice");
  const insertAt = adjIdx >= 0 ? adjIdx + 1 : without.length;
  return [
    ...without.slice(0, insertAt),
    ...trailingCols,
    ...without.slice(insertAt),
  ];
};

const filterAllReportColumns = (columns: FieldColumn[]): FieldColumn[] =>
  columns.filter((c) => !AIF_ALL_HIDDEN_COLUMN_KEYS.has(c.key));

const appendAifBenchmarkAndCostColumns = (
  columns: FieldColumn[],
  options?: { placeDaysBeforeStt?: boolean },
): FieldColumn[] => {
  let cols = columns;
  if (options?.placeDaysBeforeStt) {
    cols = stripDaysColumn(cols);
  }
  cols = stripMemberNameColumn(cols);
  if (options?.placeDaysBeforeStt) {
    cols = orderDaysColumnBeforeStt(ensureReportDaysColumn(cols));
  }
  return cols;
};

const buildAifHoldingPreset = (): FieldColumn[] => {
  let cols = appendAdjustedCostPriceColumn(
    withUnrealizedCagrColumn(AIF_UNREALIZED_HOLDING_COLUMNS),
  );
  cols = appendHoldingLevelCostColumns(cols, AIF_TABLE_SEGMENT);
  cols = appendTxnBenchmarkColumns(cols);
  return appendEquityExtraBenchmarkColumns(cols);
};

const buildAifHoldingColumns = (): FieldColumn[] =>
  appendAifBenchmarkAndCostColumns(
    withUnrealizedHoldingTypeColumn(buildAifHoldingPreset()),
    { placeDaysBeforeStt: true },
  );

const buildAifRealizedColumns = (): FieldColumn[] => {
  let cols = getColumnsForAifReportType("Realized");
  cols = withTxnMetadataAndBenchmarkColumns(cols, AIF_TABLE_SEGMENT, {
    includeBenchmarks: true,
  }) as FieldColumn[];
  cols = withUnrealizedCagrColumn(cols);
  cols = appendEquityExtraBenchmarkColumns(cols);
  return stripMemberNameColumn(cols);
};

const buildAifAllColumns = (): FieldColumn[] => {
  let cols = filterAllReportColumns(
    appendTxnBenchmarkColumns(
      appendHoldingLevelCostColumns(
        appendAdjustedCostPriceColumn(AIF_ALL_COMMON_COLUMNS),
        AIF_TABLE_SEGMENT,
      ),
    ),
  );
  cols = ensureReportDaysColumn(cols);
  cols = appendEquityExtraBenchmarkColumns(cols);
  cols = orderTrailingColumnsAfterAdjCost(cols, AIF_ALL_REPORT_AFTER_ADJ_COLUMNS);
  return appendAifBenchmarkAndCostColumns(cols, { placeDaysBeforeStt: true });
};

/** Place CAGR after AIF metadata and before Nifty/Sensex (aumsampaan Transaction). */
const relocateCagrBeforeBenchmarks = (columns: FieldColumn[]): FieldColumn[] => {
  const cagrCol =
    columns.find((c) => c.key === "cagr") ?? UNREALIZED_CAGR_COLUMN;
  const withoutCagr = columns.filter((c) => c.key !== "cagr");
  const benchmarkKeys = new Set<string>(ALL_TXN_BENCHMARK_COLUMN_KEYS);
  const firstBenchmarkIdx = withoutCagr.findIndex((c) =>
    benchmarkKeys.has(c.key),
  );
  const insertAt =
    firstBenchmarkIdx >= 0 ? firstBenchmarkIdx : withoutCagr.length;
  return [
    ...withoutCagr.slice(0, insertAt),
    cagrCol,
    ...withoutCagr.slice(insertAt),
  ];
};

const buildAifTransactionColumns = (): FieldColumn[] => {
  let cols = withTxnSchemeNameColumn(
    appendEquityExtraBenchmarkColumns(
      withTxnMetadataAndBenchmarkColumns(
        AIF_TRANSACTION_COLUMNS,
        AIF_TABLE_SEGMENT,
        { includeBenchmarks: true },
      ) as FieldColumn[],
    ),
  );
  cols = relocateCagrBeforeBenchmarks(stripDaysColumn(cols));
  return stripMemberNameColumn(cols);
};

export const getBaseColumnsForReportType = (reportType: string): FieldColumn[] => {
  switch (reportType) {
    case "Holding":
      return buildAifHoldingColumns();
    case "Realized":
      return buildAifRealizedColumns();
    case "All":
      return buildAifAllColumns();
    case "Transaction":
      return buildAifTransactionColumns();
    case AIF_CONSOLIDATED_REPORT:
      return AIF_CONSOLIDATED_COLUMNS;
    default:
      return getColumnsForAifReportType(reportType);
  }
};

export const buildSelectableColumns = (reportType: string): FieldColumn[] => [
  INVESTOR_COLUMN,
  ...getBaseColumnsForReportType(reportType),
];

export const buildEffectiveColumns = (
  reportType: string,
  visibleColumnKeys: string[],
): FieldColumn[] => {
  let columns = buildSelectableColumns(reportType);
  if (visibleColumnKeys.length > 0) {
    const visible = new Set(visibleColumnKeys);
    columns = columns.filter((c) => visible.has(c.key));
  }
  return columns;
};

export const getDefaultVisibleColumnKeys = (reportType: string): string[] => {
  const keys = buildSelectableColumns(reportType).map((c) => c.key);
  if (reportType === "Transaction") return keys;
  const hiddenExtra = new Set<string>(EQUITY_EXTRA_BENCHMARK_COLUMN_ORDER);
  return keys.filter((key) => !hiddenExtra.has(key));
};
